import { Merchant, User, Registration } from '@stax-core/types-omni';
import { refundPolicies, companyTypes, isSoleProp } from './registration.util';
import {
  getCountry,
  usaStates as states,
} from '@stax-core/lateralus-toolkit';
import { originUrl } from './api.util';
import {
  formatFullNameToFirstAndLastName,
  formatToPlainNumber,
} from './format.util';
import { uuid } from './string.util';
import { PAYFAC_PROCESSOR_APPLICATION } from './merchant.util';

// prettier-ignore
type FormValues = { [key: string]: any } & 
  Partial<User> &
  Partial<Merchant> &
  Omit<Partial<Registration>, 'company_type' | 'plan'> & {
    company_type?: Registration['company_type'] | string; 
  }

function convertStateNameToStateCode(stateName: string): string {
  switch (true) {
    case !stateName:
      return '';
    case stateName.length && stateName.length === 2:
      return stateName.toUpperCase();
    default:
      const state = states.find(
        s => s.name.toLowerCase() === stateName.toLowerCase(),
      );
      return state ? state.abbr : '';
  }
}

function convertRefundPolicyNameToRefundPolicyCode(
  refundPolicyName: string,
): Registration['refund_policy'] {
  const policy = refundPolicyName ? refundPolicyName.toLowerCase() : '';

  switch (true) {
    case policy.includes('no refunds'):
      return refundPolicies[0].key;
    case policy.includes('exchange'):
      return refundPolicies[1].key;
    case policy.includes('30'):
      return refundPolicies[2].key;
    case policy.includes('other'):
      return refundPolicies[3].key;
    default:
      return '';
  }
}

export function mapSignupFormValuesToUser(formValues: FormValues) {
  const password = uuid();

  // prettier-ignore
  return {
    name: formValues.mct_UserName || formValues.name || `${formValues.first_name} ${formValues.last_name}`,
    email: formValues.mct_Email || formValues.email || formValues.contact_email,
    brand: formValues.brand || formValues.Brand || 'fattmerchant',
    password: password,
    password_confirmation: password
  };
}

export function mapSignupFormValuesToMerchant(formValues: FormValues) {
  const state = convertStateNameToStateCode(
    formValues.mct_bill_state || formValues.address_state,
  );

  // Three states, not two. An absent flag is *not* the same as an inbound "no":
  // the core API defaults a new fattmerchant-brand merchant to payfac, and only
  // an explicit `is_payfac` in the payload opts out of that. Sending `false` for
  // a lead that never specified would suppress the default and land the merchant
  // on the ISO signing path.
  const payfacFlag = (
    formValues.Payfac ||
    formValues.is_payfac ||
    ''
  ).toLowerCase();
  let isPayfac: boolean | undefined =
    payfacFlag === '' ? undefined : payfacFlag === 'yes';
  let source = (formValues.source || formValues.Source || '').toLowerCase();
  let brand = formValues.brand || formValues.Brand || 'fattmerchant';

  if (source && source.includes('leveragecrm')) {
    brand = 'leveragecrm';
    isPayfac = true;
  }

  // prettier-ignore
  return {
    contact_name: formValues.mct_UserName || formValues.contact_name,
    company_name: formValues.mct_b_dba || formValues.mct_b_legal || formValues.company_name,
    contact_phone: String(formValues.mct_b_phone || formValues.mct_bill_phone || formValues.contact_phone || '').replace(/[\D]/g, ''),
    contact_email: formValues.mct_Email || formValues.contact_email || formValues.email,
    address_1: formValues.mct_bill_address1 || formValues.mct_b_street || formValues.address_1,
    address_2: formValues.mct_bill_address2 || formValues.address_2,
    address_city: formValues.mct_bill_city || formValues.address_city,
    address_state: state,
    address_zip: formValues.mct_bill_zip || formValues.address_zip,
    address_country: getCountry(formValues.mct_bill_country || formValues.address_country)?.iso || '',
    brand: brand,
    // Omitted when the lead didn't specify, so the API's brand-based default applies.
    ...(isPayfac === undefined ? {} : { is_payfac: isPayfac }),
    // Pin the processor application alongside `is_payfac` so the two can never
    // disagree. `merchant_processor_application` is what the app reads to pick the
    // signing path, so a PayFac merchant created without it would fall through to
    // the ISO/HelloSign path. Non-PayFac leads get no value here — underwriting
    // assigns the specific processor later.
    ...(isPayfac ? { merchant_processor_application: PAYFAC_PROCESSOR_APPLICATION } : {})
  }
}

export function mapSignupFormValuesToRegistration(formValues: FormValues) {
  let companyType = formValues.mct_ctype || formValues.company_type || '';
  let matchedCompanyType;

  if (companyType) {
    switch (companyType) {
      case 'Corporation':
        matchedCompanyType = { key: 'V', description: 'Private Corporation' };
        break;
      default:
        matchedCompanyType = companyTypes.find(type =>
          type.description.includes(companyType),
        );
    }

    companyType = matchedCompanyType ? matchedCompanyType.key : companyType;
  }

  const isFlatRateFlag = (
    formValues.checkbox_AppTypeFlatRate ||
    (formValues.is_flat_rate ? 'yes' : undefined) ||
    ''
  ).toLowerCase();

  let isFlatRate = isFlatRateFlag === 'yes' || isFlatRateFlag === 'x';
  let perTransactionFee = formValues.plantxamnt || formValues.plan_txamnt;
  let amexPerTransactionFee = perTransactionFee;
  let discountFee = 0;
  let amexDiscountFee = 0.3;
  let source = (formValues.source || formValues.Source || '').toLowerCase();
  const pricingPlan = (formValues.pricing || '').toLowerCase();

  switch (true) {
    case pricingPlan && pricingPlan.length > 0:
      // if pricing plan is passed through, don't run any of the other cases
      break;
    case source.includes('apollo'):
      perTransactionFee = 0;
      discountFee = 15;
      amexDiscountFee = 15;
      break;
    case source.includes('leveragecrm'):
      perTransactionFee = 0.25;
      discountFee = 2.9;
      amexPerTransactionFee = 0.25;
      amexDiscountFee = 3.5;
      break;
    case source.includes('prognocis'):
      perTransactionFee = 0.15;
      discountFee = 0;
      amexPerTransactionFee = 0.15;
      amexDiscountFee = 0.3;
      isFlatRate = false;
      break;
    case source.includes('hindsite'):
      perTransactionFee = 0.3;
      discountFee = 2.9;
      amexPerTransactionFee = 0.3;
      amexDiscountFee = 3.5;
      isFlatRate = true;
      break;
    case isFlatRate:
      perTransactionFee = 0.15;
      discountFee = 2.9;
      amexDiscountFee = 2.9;
      break;
  }

  let [firstName, lastName] = formatFullNameToFirstAndLastName(
    formValues.mct_UserName,
  );
  firstName = firstName || formValues.first_name || '';
  lastName = lastName || formValues.last_name || '';

  // prettier-ignore
  return {
    is_flat_rate: isFlatRate,

    pricing_plan: pricingPlan || null,

    // $ per transaction
    plan_txamnt: `${perTransactionFee || ''}`,
    credit_mid_trans_fee: `${perTransactionFee || ''}`,
    credit_non_trans_fee: `${perTransactionFee || ''}`,
    credit_qual_trans_fee: `${perTransactionFee || ''}`,
    amex_qual_trans_fee: `${amexPerTransactionFee || ''}`,
    amex_mid_trans_fee: `${amexPerTransactionFee || ''}`,
    amex_non_trans_fee: `${amexPerTransactionFee || ''}`,

    // Pricing: % per transaction
    plan_dcamnt: `${discountFee || ''}`,
    credit_qual_disc_rate: `${discountFee || ''}`,
    credit_mid_disc_rate: `${discountFee || ''}`,
    credit_non_disc_rate: `${discountFee || ''}`,
    amex_qual_disc_rate: `${amexDiscountFee || ''}`,
    amex_mid_disc_rate: `${amexDiscountFee || ''}`,
    amex_non_disc_rate: `${amexDiscountFee || ''}`,

    refund_policy: convertRefundPolicyNameToRefundPolicyCode(formValues.mct_bd_refund || formValues.refund_policy),

    // Business Information
    business_legal_name: formValues.mct_b_legal || formValues.business_legal_name,
    business_email: formValues.business_email || formValues.email || formValues.mct_email || formValues.contact_email,
    business_dba: formValues.mct_b_dba || formValues.business_dba,
    business_website: String(formValues.mct_b_web || formValues.business_website || '').split('?')[0],
    business_phone_number: formValues.mct_b_phone || formValues.mct_bill_phone || formValues.business_phone_number,
    business_fax: formValues.BusinessBillingFax || formValues.mct_b_fax || formValues.business_fax,

    // Business Adress
    business_address_1: formValues.mct_bill_address1 || formValues.mct_b_street || formValues.business_address_1,
    business_address_2: formValues.mct_bill_address2 || formValues.business_address_2,
    business_address_city: formValues.mct_bill_city || formValues.business_address_city,
    business_address_state: convertStateNameToStateCode(formValues.mct_bill_state || formValues.business_address_state),
    business_address_zip: formValues.mct_bill_zip || formValues.business_address_zip,
    business_address_country: getCountry(formValues.mct_bill_country || formValues.business_address_country)?.iso|| '',
    business_location_address_1: formValues.mct_bill_address1 || formValues.mct_b_street || formValues.business_location_address_1,
    business_location_address_2: formValues.mct_bill_address2 || formValues.business_location_address_2,
    business_location_address_city: formValues.mct_bill_city || formValues.business_location_address_city,
    business_location_address_state: convertStateNameToStateCode(formValues.mct_bill_state || formValues.business_location_address_state),
    business_location_address_zip: formValues.mct_bill_zip || formValues.business_location_address_zip,
    business_location_address_country: getCountry(formValues.mct_bill_country || formValues.business_location_address_country)?.iso|| '',
    business_location_phone_number: formValues.mct_bill_phone || formValues.business_location_phone_number,

    business_open_date: formValues.mct_bd_open || formValues.business_open_date,
    company_type: companyType,
    business_tax_id: formValues.mct_bd_taxid || formValues.business_tax_id,

    annual_volume: formatToPlainNumber(formValues.mct_v_annual || formValues.annual_volume),
    avg_trans_size: formatToPlainNumber(formValues.mct_v_avgtx || formValues.avg_trans_size),
    highest_trans_amount: formatToPlainNumber(formValues.mct_v_highesttx || formValues.highest_trans_amount),
    card_present_percent: formatToPlainNumber(formValues.mct_v_present || formValues.v_present || formValues.card_present_percent),
    card_swiped_percent: formatToPlainNumber(formValues.mct_v_swiped || formValues.v_swiped || formValues.card_swiped_percent),
    card_not_present_percent: formatToPlainNumber(formValues.mct_v_notpresent || formValues.v_notpresent || formValues.card_not_present_percent),
    moto_percent: formatToPlainNumber(formValues.mct_v_moto || formValues.v_moto || formValues.moto_percent),

    internet: formatToPlainNumber(formValues.mct_v_internet || formValues.mct_v_net || formValues.internet),
    b2b_percent: formatToPlainNumber(formValues.mct_v_B2B || formValues.b2b_percent),
    international: formatToPlainNumber(formValues.mct_v_international || formValues.international),

    bank_routing_number: formValues.mct_v_routing || formValues.bank_routing_number,
    bank_account_number: formValues.mct_v_account || formValues.bank_account_number,

    chosen_plan: formValues.mct_PlanChoice || formValues.chosen_plan,
    chosen_processing_method: formValues.ProcessingMethod || formValues.chosen_processing_method,

    reason_for_applying: formValues.mct_bd_whyapplying || formValues.mct_reason || formValues.reason_for_applying,
    service_you_provide: formValues.mct_bd_whatsold || formValues.service_you_provide,

    plan_nabu: formValues.NABU || formValues.plan_nabu,

    bus_type: formValues.mct_btype || formValues.bus_type,

    // business owner information
    email: formValues.mct_Email || formValues.email || formValues.contact_email,
    first_name: firstName,
    last_name: lastName,
    principal_owners_name: formValues.mct_o_principal || formValues.principal_owners_name,
    job_title: String(formValues.mct_o_title || formValues.job_title || '').replace(/[^a-z\d ]+/gi, ' '),
    user_dob: formValues.OwnerDOB || formValues.user_dob,
    phone_number: formValues.OwnerNumber || formValues.phone_number,
    // user_ssn: formValues.ons || formValues.user_ssn,

    // business address information
    owner_address_1: formValues.OwnerAddress1 || formValues.owner_address_1,
    owner_address_2: formValues.OwnerAddress2 || formValues.owner_address_2,
    owner_address_city: formValues.OwnerCity || formValues.owner_address_city,
    owner_address_state: convertStateNameToStateCode(formValues.OwnerState || formValues.owner_address_state),
    owner_address_country: getCountry(formValues.OwnerState || formValues.owner_address_country)?.iso|| '',
    owner_address_zip: formValues.OwnerZip || formValues.owner_address_zip,

    meta: { 
      ownership_percentage: '100',
      is_new_app: true,
    }
  }
}

export function mapSignupFormValuesToRegisterPayload(
  formValues: FormValues,
): FormValues {
  const registrationValues = mapSignupFormValuesToRegistration(formValues);
  // prettier-ignore
  return {
    ...registrationValues,
    ...mapSignupFormValuesToMerchant(formValues),
    ...mapSignupFormValuesToUser(formValues),
    app_url: originUrl('sso/magiclink/'),
    registration_source: isSoleProp(registrationValues) && formValues.brand === 'stax'
      ? 'gogurt'
      : 'self-registration', // NOTE: the API may change this to 'gogurt' if the right criteria are met
  };
}
